"use server";

import { z } from "zod";
import { defineAction } from "@/lib/define-action";
import { prisma } from "@/lib/prisma";
import { usersWithPermission } from "@/lib/access";
import { notify } from "@/lib/notify";
import { actionKey } from "@/lib/notification-action-kinds";

const fail = (error: string) => ({ ok: false as const, error });

/** Clients a person can ask a project for. */
export const listClientsForProjectRequest = defineAction({
  guard: "time:log",
  run: async () => prisma.client.findMany({ where: { deletedAt: null }, orderBy: { name: "asc" }, select: { id: true, name: true } }),
});

/** "No project, no time" (D48): a person whose client has no project asks the PM admins for one; the request stays open until a project exists. */
export const requestProjectAction = defineAction({
  guard: "time:log",
  safe: "Could not send the request.",
  input: z.object({ clientId: z.number().int().positive(), note: z.string().trim().max(500).optional() }),
  run: async ({ userId }, input: { clientId: number; note?: string }) => {
    const [client, user] = await Promise.all([
      prisma.client.findFirst({ where: { id: input.clientId, deletedAt: null }, select: { id: true, name: true } }),
      prisma.user.findUnique({ where: { id: userId }, select: { name: true, email: true } }),
    ]);
    if (!client) return fail("This client no longer exists.");
    const live = await prisma.hubProject.count({ where: { clientId: client.id, deletedAt: null, rejectedAt: null, status: { in: ["DRAFT", "ACTIVE", "ON_HOLD"] } } });
    if (live > 0) return fail(`${client.name} already has a project. Reload the list and pick it.`);
    const key = actionKey("project_request", client.id);
    const open = await prisma.notification.count({ where: { actionKey: key, resolvedAt: null } });
    if (open > 0) return { ok: true as const, already: true as const };
    const admins = await usersWithPermission("projects:approve");
    if (admins.length === 0) return fail("There is no PM admin to ask right now.");
    await notify({
      type: "project_request",
      category: "projects",
      recipients: admins,
      message: `${user?.name || user?.email || "Someone"} needs a project for ${client.name} to log time.${input.note ? ` Note: ${input.note}` : ""}`,
      link: "/projects/new",
      actionKey: key,
    });
    return { ok: true as const, already: false as const };
  },
});
